import clsx from 'clsx'
import { useField } from 'formik'
import { FC, useEffect, useState } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { ErrorCode, useDropzone } from 'react-dropzone'

import { FilePreview } from './FilePreview'

export interface InputFileProps {
  accept: { [key: string]: string[] }
  className?: string
  filePreviewClassName?: string
  gridClassName?: string
  hint?: string
  id: string
  isInputGridItem?: boolean
  isPreview?: boolean
  label?: string
  maxFiles: number
  maxSize?: number
  name: string
  onAdd?(files: File[]): void
  onRearrange?(images: (File | { id: number; src: string })[]): void
  onRemove?(file: File | { id: number; src: string }): void
  renderImage?: FC<{ src: string }>
  renderInput?: FC
  renderLabel?: FC
  type?: string
}

export function InputFile({
  id,
  name,
  label,
  maxFiles,
  maxSize,
  type,
  hint: propsHint,
  isPreview,
  isInputGridItem,
  accept,
  onAdd,
  onRearrange,
  onRemove,
  renderImage,
  renderLabel: Label,
  renderInput: Input,
  filePreviewClassName = '',
  gridClassName = '',
  className = '',
}: InputFileProps) {
  const [{ value }, { error }, { setValue, setError }] = useField<(File | { id: number; src: string })[]>({
    type: 'file',
    name,
  })
  const [files, setFiles] = useState<(File | { id: number; src: string })[]>(value || [])
  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop: handleUploadDrop,
    maxFiles,
    accept,
    maxSize,
  })
  const numberUploaded = files.length
  const isError = !!error
  const hint = (isError && error) || propsHint

  useEffect(() => {
    setError(undefined)
    setValue(files)

    fileRejections.map(({ errors }) =>
      errors.map(({ code }) => {
        if (code === ErrorCode.FileTooLarge && maxSize) setError(`Max file size ${maxSize / 1000000} MB`)
      }),
    )
    // * useField is not a useCallback
    // eslint-disable-next-line
  }, [files])

  function handleUploadDrop(acceptedFiles: File[]) {
    setFiles([...files, ...acceptedFiles])
    onAdd && onAdd(acceptedFiles)
  }

  function handleClickRemove(file: File | { id: number; src: string }) {
    const updatedFiles = [...files]
    updatedFiles.splice(updatedFiles.indexOf(file), 1)

    setFiles(updatedFiles)
    onRemove && onRemove(file)
  }

  function handleMoveFilePreview(dragIndex: number, hoverIndex: number) {
    const rearrangedFiles = [...files]
    const dragFile = rearrangedFiles[dragIndex]
    rearrangedFiles[dragIndex] = rearrangedFiles[hoverIndex]
    rearrangedFiles[hoverIndex] = dragFile

    setFiles(rearrangedFiles)
    onRearrange && onRearrange(rearrangedFiles)
  }

  function renderUploadedTotal() {
    return (
      <p className="mt-1">
        {numberUploaded}/{maxFiles} {type} uploaded
      </p>
    )
  }

  function renderActiveText() {
    return <p>Drop File(s)</p>
  }

  function renderInActiveText() {
    return (
      <p>
        Drop files here or{' '}
        <span
          className={clsx(
            'font-bold',
            isError
              ? 'text-semantic-error'
              : 'text-link hover:text-link-deemphasized focus-visible:text-link-deemphasized',
          )}
        >
          browse
        </span>
      </p>
    )
  }

  function renderInput() {
    return (
      <div
        className={clsx(
          'flex cursor-pointer items-center justify-center rounded-lg border-2 border-dashed',
          isError ? 'border-error bg-inline-error' : 'border-secondary bg-primary',
        )}
      >
        <div
          className={clsx(
            'flex h-20 flex-col items-center justify-center font-medium leading-120',
            isError ? 'text-semantic-error' : 'text-tertiary',
          )}
        >
          {isDragActive ? renderActiveText() : renderInActiveText()}
          {!!numberUploaded && renderUploadedTotal()}
        </div>
      </div>
    )
  }

  return (
    <div className={className}>
      <input
        {...getInputProps()}
        className="invisible block h-0 overflow-hidden opacity-0"
        data-testid={id}
        id={id}
        name={name}
        style={{}}
      />

      {(label || Label) && (
        <label className="text-primary block mb-2 font-medium leading-120" htmlFor={id}>
          {Label ? <Label /> : label}
        </label>
      )}

      {!isInputGridItem && (
        <label htmlFor={`file-upload-${id}`} {...getRootProps()}>
          {Input ? <Input /> : renderInput()}
        </label>
      )}

      {hint && (
        <div
          className={clsx(
            'mt-2 flex font-medium text-p-xs leading-120',
            hint ? 'justify-between' : 'justify-end',
            isError ? 'text-semantic-error' : 'text-tertiary',
          )}
        >
          <p>{hint}</p>
        </div>
      )}

      {isPreview && (
        <div className={clsx('grid gap-4 grid-flow-row mt-2', gridClassName)}>
          <DndProvider backend={HTML5Backend}>
            {files.map((file, i) => (
              <div
                className={clsx('h-28', filePreviewClassName)}
                key={(file as any)?.src ? (file as any).src : (file as any).name}
              >
                <FilePreview
                  file={file}
                  index={i}
                  onClickRemove={handleClickRemove}
                  onMoveFilePreview={handleMoveFilePreview}
                  renderImage={renderImage}
                  totalFiles={files.length}
                />
              </div>
            ))}
          </DndProvider>

          {isInputGridItem && (
            <label htmlFor={`file-upload-${id}`} {...getRootProps()}>
              {Input ? <Input /> : renderInput()}
            </label>
          )}
        </div>
      )}
    </div>
  )
}
